import React from 'react';
import { Chip } from '@mui/material';
import { RESOURCES } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import ConfigSection from '../ConfigSection';
import { AccountPicker } from '../LedgerPickers';

// Stock items: the master list (verified against CreateInventoryItemDto).
// category is free text, uom defaults to PCS, costing_method is required
// (STANDARD needs a standard_cost). The three GL legs are all required:
// inventory (BS asset), COGS, and adjustment (write-off/variance/count
// differences). The list carries live valuation (quantity_on_hand,
// average_unit_cost, stock_value). Updates can only touch name, description,
// category, standard_cost and the active flag.
const costingMethods = ['FIFO', 'WEIGHTED_AVERAGE', 'STANDARD'].map((m) => ({
  label: m.replace(/_/g, ' '),
  value: m,
}));

const descriptor = {
  resource: RESOURCES.LEDGER.INVENTORY,
  title: 'Stock Items',
  subtitle: 'Master list - every stocked, consumable or sold item with live valuation',
  idKey: 'item_id',
  searchPlaceholder: 'Search code or name...',
  columns: [
    { label: 'Code', render: (i) => <strong>{i.item_code}</strong> },
    { label: 'Name', render: (i) => i.item_name },
    { label: 'Category', render: (i) => i.category || '-' },
    { label: 'Method', render: (i) => (i.costing_method || '').replace(/_/g, ' ') || '-' },
    { label: 'On Hand', render: (i) => i.quantity_on_hand ?? '-', align: 'right' },
    { label: 'Unit Cost', render: (i) => formatAccountingAmount(i.average_unit_cost ?? i.unit_cost), align: 'right' },
    { label: 'Stock Value', render: (i) => formatAccountingAmount(i.stock_value ?? i.total_cost_value), align: 'right' },
    {
      label: 'Status',
      render: (i) => (
        <Chip label={i.is_active === false ? 'Inactive' : 'Active'} color={i.is_active === false ? 'default' : 'success'} size="small" />
      ),
    },
  ],
  fields: [
    { key: 'item_code', label: 'Item Code', required: true, helper: 'Unique, 2-40 chars - locked after creation', md: 6 },
    { key: 'item_name', label: 'Item Name', required: true, md: 6 },
    { key: 'category', label: 'Category', helper: 'Free text, e.g. STATIONERY', md: 6 },
    { key: 'uom', label: 'Unit of Measure', helper: 'Defaults to PCS - locked after creation', md: 6 },
    { key: 'costing_method', label: 'Costing Method', type: 'select', options: costingMethods, required: true, helper: 'Locked after creation', md: 6 },
    { key: 'standard_cost', label: 'Standard Cost (required for STANDARD)', type: 'number', min: 0, md: 6 },
    { key: 'inventory_account_id', label: 'Inventory Account (BS asset)', type: 'picker', picker: AccountPicker, required: true, md: 6 },
    { key: 'cogs_account_id', label: 'COGS Account', type: 'picker', picker: AccountPicker, required: true, md: 6 },
    { key: 'adjustment_account_id', label: 'Adjustment Account (write-off/variance)', type: 'picker', picker: AccountPicker, required: true, md: 6 },
    { key: 'description', label: 'Description', type: 'textarea', md: 12 },
    { key: 'is_active', label: 'Active', type: 'switch', md: 12 },
  ],
  initialValues: {
    item_code: '', item_name: '', category: '', uom: '', costing_method: 'FIFO',
    standard_cost: '', inventory_account_id: '', cogs_account_id: '',
    adjustment_account_id: '', description: '', is_active: true,
  },
  fetchList: (params) => ledgerAPI.getInvItems(params),
  createItem: (payload) => ledgerAPI.createInvItem(payload),
  updateItem: (id, payload) => ledgerAPI.updateInvItem(id, payload),
  deleteItem: (id) => ledgerAPI.deleteInvItem(id),
  deleteMessage: (item) => `Delete item ${item.item_code}? Only items with no movements can be deleted - otherwise deactivate it.`,
  mapToForm: (item) => ({
    // Code, UoM, method and GL legs are immutable after creation - only the
    // UpdateInventoryItemDto fields are mapped back for editing.
    item_code: item.item_code || '',
    item_name: item.item_name || '',
    category: item.category || '',
    uom: item.uom || '',
    costing_method: item.costing_method || 'FIFO',
    standard_cost: item.standard_cost ?? '',
    inventory_account_id: item.inventory_account_id ?? '',
    cogs_account_id: item.cogs_account_id ?? '',
    adjustment_account_id: item.adjustment_account_id ?? '',
    description: item.description || '',
    is_active: item.is_active !== false,
  }),
  mapToPayload: (values, isEdit) => {
    if (isEdit) {
      const payload = {};
      if (values.item_name !== undefined) payload.item_name = values.item_name;
      if (values.description !== undefined) payload.description = values.description;
      if (values.category !== undefined) payload.category = values.category;
      if (values.standard_cost !== '' && values.standard_cost !== undefined) payload.standard_cost = Number(values.standard_cost);
      if (values.is_active !== undefined) payload.is_active = values.is_active;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) delete payload[k];
      });
      return payload;
    }
    const out = { ...values };
    Object.keys(out).forEach((k) => {
      if (out[k] === '' || out[k] === undefined) delete out[k];
    });
    if (out.standard_cost !== undefined) out.standard_cost = Number(out.standard_cost);
    ['inventory_account_id', 'cogs_account_id', 'adjustment_account_id'].forEach((k) => {
      if (out[k] !== undefined) out[k] = Number(out[k]);
    });
    return out;
  },
  // ConfigSection calls validate(values) in both modes; mapToForm repopulates
  // every field on edit, so create-rules hold there too, while
  // mapToPayload strips immutable fields before PATCH.
  validate: (values) => {
    if (!String(values.item_code ?? '').trim()) return 'item code is required';
    if (!String(values.item_name ?? '').trim()) return 'item name is required';
    if (!values.costing_method) return 'costing method is required';
    if (values.costing_method === 'STANDARD' && !(Number(values.standard_cost) > 0)) {
      return 'standard cost is required for STANDARD costing';
    }
    for (const key of ['inventory_account_id', 'cogs_account_id', 'adjustment_account_id']) {
      if (!values[key]) return `${key.replace(/_/g, ' ')} is required`;
    }
    return null;
  },
};

const ItemsTab = () => <ConfigSection descriptor={descriptor} />;

export default ItemsTab;
